/** 矩形，和 `getBoundingClientRect()` 同一个坐标系 */
export interface Rect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PopoverPlacement {
  /** 面板开在触发器下面还是上面 */
  side: 'below' | 'above';
  /** 面板最多多高，超出的在面板里滚动 */
  maxHeight: number;
  /** 面板相对触发器左边要挪多少（负数往左） */
  offsetX: number;
}

/** 两边都挤到这个高度以下时不再往小里压 —— 再矮一行都显示不全，面板等于没用 */
export const MIN_POPOVER_HEIGHT = 96;

/**
 * 自定义下拉面板往哪开、多高、横向挪多少，才不出可视区（DESIGN-SYSTEM「下拉面板」）。
 *
 * - 下面放得下就往下开；放不下时哪边空间大往哪边开，高度封顶到那边的空间，内容在面板里滚；
 * - 横向和触发器左对齐，右边出屏就往左挪，但不越过左边距；
 * - `viewport` 传可视区：手机键盘弹出来时它比窗口矮；`margin` 是离屏幕边缘至少留多少；
 * - `maxHeight` 是组件自己的上限（地址候选最多 18rem），空间再大也不超过它。
 *
 * 改之前两个自定义下拉都写死往下开：手机横屏、页面滚到底、键盘弹出时面板出屏两三百像素；
 * 采购、考勤页的区间按钮前面有「日期范围」，手机上面板右边还出屏 37px，整页横着多出一条。
 */
export function placePopover({
  anchor,
  panel,
  viewport,
  gap = 4,
  margin = 8,
  maxHeight,
}: {
  anchor: Rect;
  panel: { width: number; height: number };
  viewport: Rect;
  gap?: number;
  margin?: number;
  maxHeight?: number;
}): PopoverPlacement {
  const wanted = maxHeight === undefined ? panel.height : Math.min(panel.height, maxHeight);
  const spaceBelow = viewport.bottom - margin - (anchor.bottom + gap);
  const spaceAbove = anchor.top - gap - (viewport.top + margin);
  const side = wanted <= spaceBelow || spaceBelow >= spaceAbove ? 'below' : 'above';
  const room = Math.max(MIN_POPOVER_HEIGHT, Math.floor(side === 'below' ? spaceBelow : spaceAbove));

  const rightLimit = viewport.right - margin;
  let left = anchor.left;
  if (left + panel.width > rightLimit) left = rightLimit - panel.width;
  if (left < viewport.left + margin) left = viewport.left + margin;

  return {
    side,
    maxHeight: maxHeight === undefined ? room : Math.min(room, maxHeight),
    offsetX: Math.round(left - anchor.left),
  };
}
